import { css } from 'lit';

/**
 * Dark theme — a SEPARATE constructable stylesheet, adopted into the widget's
 * shadow root only when the owner picked `color_scheme: 'dark'` (see
 * ChatWidget.updated). Keeping it out of the base `static styles` means
 * light-mode widgets never parse these rules.
 *
 * The base stylesheet drives every surface from CSS custom properties
 * (`--bg`, `--surface`, `--surface-2`, `--ink`, `--ink-muted`, `--border`), so
 * dark mode is just a handful of variable overrides scoped under [data-theme='dark'].
 * Tenant brand colors (`--primary`, `--accent`) are intentionally untouched.
 *
 * The neutral-derived tokens in the base sheet mix the brand toward #ffffff
 * (light surfaces). In dark mode that inverts wrong, so --surface-2 and
 * --accent-soft are re-anchored to dark bases here. Brand-derived coral/plum
 * tokens (--accent-strong, --primary-2, gradients) read fine on dark and stay.
 */
export const darkStyles = css`
  :host([data-theme='dark']) {
    --bg: #17141d;
    --surface: #221d2b;
    --surface-2: #17141d;
    --ink: #ece9f1;
    --ink-muted: #9b96a8;
    --border: rgba(255, 255, 255, 0.12);
    --accent-soft: color-mix(in srgb, var(--accent) 22%, #221d2b);
    /* Secondary buttons re-anchored to a dark plum surface with light ink (the
       light-mode mix toward #ffffff would invert too bright here). */
    --secondary: color-mix(in srgb, var(--primary) 32%, #2a2433);
    --secondary-hover: color-mix(in srgb, var(--primary) 44%, #2a2433);
    --secondary-ink: color-mix(in srgb, #ffffff 84%, var(--primary));
    --secondary-border: rgba(255, 255, 255, 0.14);
  }
  /* Round close (X) buttons (peek sheet, nudge bubble): the light-mode plum
     overlay vanishes on the dark surface — re-anchor to a white overlay. */
  :host([data-theme='dark']) .x-close {
    background: rgba(255, 255, 255, 0.08);
  }
  :host([data-theme='dark']) .x-close:hover,
  :host([data-theme='dark']) .x-close:active {
    background: rgba(255, 255, 255, 0.16);
  }
`;
